import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PartyPopper, Loader2, AlertCircle } from 'lucide-react';
import { laborScheduleService } from '../services/laborScheduleService';
import { formatCalendarOption } from '../utils/laborCalendarUtils';
import { LaborCalendarModalShell } from './LaborCalendarModalShell';

const HOLIDAY_TYPES = [
  { value: 'national', label: 'Nacional' },
  { value: 'regional', label: 'Regional' },
  { value: 'local', label: 'Local' },
  { value: 'company', label: 'Empresa' },
];

export function LaborAddHolidayModal({
  isOpen,
  onClose,
  factoryId,
  factoryCode,
  defaultYear,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const [calendarId, setCalendarId] = useState('');
  const [date, setDate] = useState(`${defaultYear}-01-01`);
  const [name, setName] = useState('');
  const [holidayType, setHolidayType] = useState('national');
  const [isRecoverable, setIsRecoverable] = useState(false);
  const [error, setError] = useState(null);

  const { data: calendars = [], isLoading: loadingCalendars } = useQuery({
    queryKey: ['labor-calendars', factoryId],
    queryFn: () => laborScheduleService.listCalendarsByFactory(factoryId),
    enabled: isOpen && Boolean(factoryId),
  });

  useEffect(() => {
    if (isOpen) {
      setDate(`${defaultYear}-01-01`);
      setName('');
      setHolidayType('national');
      setIsRecoverable(false);
      setError(null);
    }
  }, [isOpen, defaultYear]);

  useEffect(() => {
    if (!isOpen || calendars.length === 0) return;
    const match = calendars.find((c) => Number(c.year) === Number(defaultYear));
    setCalendarId(String((match || calendars[0]).id));
  }, [isOpen, calendars, defaultYear]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!name.trim()) throw new Error('El nombre del festivo es obligatorio.');
      if (!date) throw new Error('La fecha es obligatoria.');
      return laborScheduleService.createHoliday({
        calendarId,
        date,
        name,
        holidayType,
        isRecoverable,
      });
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['labor-holidays', calendarId] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-schedule'] });
      await queryClient.invalidateQueries({ queryKey: ['labor-calendar-pattern-list'] });
      onSuccess?.(data);
      onClose();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo registrar el festivo');
    },
  });

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={mutation.isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          mutation.mutate();
        }}
        disabled={mutation.isPending || !calendarId || loadingCalendars}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        Registrar festivo
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Registrar festivo"
      icon={PartyPopper}
      footer={footer}
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <p className="text-sm text-gray-400 mb-4">
        Fábrica <span className="text-white font-medium">{factoryCode}</span>. El día debe existir
        en el calendario (genera los días del año antes si hace falta).
      </p>

      <label htmlFor="labor-hol-cal" className="block text-sm text-gray-300 mb-2">
        Calendario <span className="text-red-400">*</span>
      </label>
      {loadingCalendars ? (
        <p className="text-gray-500 text-sm mb-4 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando…
        </p>
      ) : calendars.length === 0 ? (
        <p className="text-amber-400 text-sm mb-4 bg-amber-900/20 border border-amber-700/50 rounded-lg p-3">
          No hay calendarios. Crea uno y genera sus días primero.
        </p>
      ) : (
        <select
          id="labor-hol-cal"
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          disabled={mutation.isPending}
          className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm mb-4"
        >
          {calendars.map((cal) => (
            <option key={cal.id} value={cal.id}>
              {formatCalendarOption(cal)}
            </option>
          ))}
        </select>
      )}

      <label htmlFor="labor-hol-date" className="block text-sm text-gray-300 mb-2">
        Fecha <span className="text-red-400">*</span>
      </label>
      <input
        id="labor-hol-date"
        type="date"
        value={date}
        onChange={(e) => setDate(e.target.value)}
        disabled={mutation.isPending}
        className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm mb-4"
      />

      <label htmlFor="labor-hol-name" className="block text-sm text-gray-300 mb-2">
        Nombre <span className="text-red-400">*</span>
      </label>
      <input
        id="labor-hol-name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={mutation.isPending}
        placeholder="Ej. Navidad"
        className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm mb-4"
      />

      <label htmlFor="labor-hol-type" className="block text-sm text-gray-300 mb-2">
        Tipo
      </label>
      <select
        id="labor-hol-type"
        value={holidayType}
        onChange={(e) => setHolidayType(e.target.value)}
        disabled={mutation.isPending}
        className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm mb-4"
      >
        {HOLIDAY_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>

      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
        <input
          type="checkbox"
          checked={isRecoverable}
          onChange={(e) => setIsRecoverable(e.target.checked)}
          disabled={mutation.isPending}
          className="rounded border-gray-600"
        />
        Festivo recuperable (compensación en otro día)
      </label>
    </LaborCalendarModalShell>
  );
}
